import {type TCountryCode} from 'countries-list'
import {type UnionToTuple} from 'type-fest'

import {
  MedicalEquipmentType,
  MobilityAssistanceType,
  MobilityDeviceType,
  PassengerType,
  SpecialAssistanceType,
} from './types'

export const STATE_PROVINCE_REQUIRED_COUNTRIES = ['US', 'CA'] as const satisfies TCountryCode[]

export type StateProvinceRequiredCountries = (typeof STATE_PROVINCE_REQUIRED_COUNTRIES)[number]

export function isStateProvinceRequiredCountry(
  countryCode: unknown,
): countryCode is StateProvinceRequiredCountries {
  if (typeof countryCode !== 'string') return false
  return STATE_PROVINCE_REQUIRED_COUNTRIES.includes(countryCode)
}

export const FLIGHT_TYPE_MAP: Record<string, string> = {
  '1': 'Round trip',
  '2': 'One-way',
  '3': 'Multi-city',
}

export const PASSENGER_TYPE_MAP: Record<PassengerType, string> = {
  [PassengerType.Adult]: 'Adult',
  [PassengerType.Child]: 'Child',
  [PassengerType.InfantInSeat]: 'Infant in seat',
  [PassengerType.InfantOnLap]: 'Infant on lap',
}

const MOBILITY_ASSISTANCE_ORDERED: UnionToTuple<MobilityAssistanceType> = [
  MobilityAssistanceType.NeedWheelchair,
  MobilityAssistanceType.CanWalk,
  MobilityAssistanceType.CannotWalk,
  MobilityAssistanceType.PersonalWheelchair,
]

export const MOBILITY_ASSISTANCE_MAP: Record<MobilityAssistanceType, string> = {
  [MobilityAssistanceType.NeedWheelchair]: 'Need a wheelchair to and from the gate',
  [MobilityAssistanceType.CanWalk]: 'Can walk - need help only if there are stairs',
  [MobilityAssistanceType.CannotWalk]: 'Cannot walk - need wheelchair assistance',
  [MobilityAssistanceType.PersonalWheelchair]:
    "I'm bringing a personal wheelchair / mobility device",
}

export const MOBILITY_ASSISTANCE_ITEMS = MOBILITY_ASSISTANCE_ORDERED.map(type => ({
  label: MOBILITY_ASSISTANCE_MAP[type],
  value: type,
}))

export const MOBILITY_DEVICES_MAP: Record<MobilityDeviceType, string> = {
  [MobilityDeviceType.ManualWheelchair]: 'Manual wheelchair',
  [MobilityDeviceType.DryGelCellBatteryWheelchairScooter]:
    'Dry / gel cell battery wheelchair / scooter',
  [MobilityDeviceType.WetCellBatteryWheelchairScooter]: 'Wet cell battery wheelchair / scooter',
  [MobilityDeviceType.LithiumBatteryWheelchairScooter]: 'Lithium battery wheelchair / scooter',
}

const MOBILITY_DEVICES_ORDERED: UnionToTuple<MobilityDeviceType> = [
  MobilityDeviceType.ManualWheelchair,
  MobilityDeviceType.DryGelCellBatteryWheelchairScooter,
  MobilityDeviceType.WetCellBatteryWheelchairScooter,
  MobilityDeviceType.LithiumBatteryWheelchairScooter,
]

export const MOBILITY_DEVICES = MOBILITY_DEVICES_ORDERED.map(type => ({
  label: MOBILITY_DEVICES_MAP[type],
  value: type,
}))

const SPECIAL_ASSISTANCE_ORDERED: UnionToTuple<SpecialAssistanceType> = [
  SpecialAssistanceType.CognitiveDisability,
  SpecialAssistanceType.HearingAssistance,
  SpecialAssistanceType.BlindLowVision,
]

export const SPECIAL_ASSISTANCE_MAP: Record<SpecialAssistanceType, string> = {
  [SpecialAssistanceType.CognitiveDisability]: 'Cognitive / developmental disability assistance',
  [SpecialAssistanceType.HearingAssistance]: 'Hearing assistance',
  [SpecialAssistanceType.BlindLowVision]: 'Blind / low vision assistance',
}

export const SPECIAL_ASSISTANCE_ITEMS = SPECIAL_ASSISTANCE_ORDERED.map(type => ({
  label: SPECIAL_ASSISTANCE_MAP[type],
  value: type,
}))

const MEDICAL_EQUIPMENT_ORDERED: UnionToTuple<MedicalEquipmentType> = [
  MedicalEquipmentType.Poc,
  MedicalEquipmentType.OtherMedicalEquipment,
]

export const MEDICAL_EQUIPMENT_MAP: Record<MedicalEquipmentType, string> = {
  [MedicalEquipmentType.Poc]: 'Traveling with portable oxygen concentrator (POC)',
  [MedicalEquipmentType.OtherMedicalEquipment]:
    'Traveling with other medical equipment (CPAP, respirator, ventilator)',
}

export const MEDICAL_EQUIPMENT_ITEMS = MEDICAL_EQUIPMENT_ORDERED.map(type => ({
  label: MEDICAL_EQUIPMENT_MAP[type],
  value: type,
}))
